import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios, { customFormAxios } from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import useConfirmationDialog from './useConfirmationDialog';
import { useToast } from 'context/tostifyContext';
import { DEFAULT_PRODUCT_DATA_VALUE, DEFAULT_SPECIES_DATA_VALUE } from '../Data/SectionDefaultData';
import { type } from '@amcharts/amcharts4/core';
import { tr } from 'date-fns/locale';
import { calculatePolygonAreaGoogle } from '@jumbo/utils/mapHelpers';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

const INITIAL_SITEDATA_STATE = {
  checklist: [],
  geolocations: [],
  harvesting: [],
  hazards: [],
  info: [],
  siteVolume: [],
  products: [],
  species: [],
  mapCoordinates: [],
};
const NOSECTION_SELECT = {
  sectionName: '',
  sectionData: '',
};
const useSiteAssessment = () => {
  const [siteAssessmentData, setSiteAssessmentData] = useState([]);
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const isMounted = useRef(true);

  //site assessment data dialog control
  const [openSiteAssessmentDialog, setOpenSiteAssessmentDialog] = useState(false);
  const [siteInfoData, setSiteInfoData] = useState(INITIAL_SITEDATA_STATE);
  const [selectedSiteData, setSelectedSiteData] = useState({});
  //editData
  const [openSectionEditModal, setOpenSectionEditModal] = useState(false);
  const [selectionSectionData, setSelectedSectionData] = useState(NOSECTION_SELECT);

  // notes
  const [notes, setNotes] = useState({
    siteNotesforOperatorsOnly: '',
    siteNotesforHaulageOnly: '',
    siteNotesforForestManagerOnly: '',
    additionalManagementNotes: '',
  });

  // files
  const [files, setFiles] = useState([]);
  const [openFilesDialog, setOpenFilesDialog] = useState(false);
  const [siteDocs, setSiteDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  //googleMapview
  const [mapData, setMapData] = useState([]);
  const [openGoogleMapView, setOpenGoogleMapView] = useState(false);

  //samples
  const [samples, setSamples] = useState([]);
  const [samplesData, setSamplesData] = useState([]);
  const [subCompSampleData, setSubCompSampleData] = useState([]);

  // Sub Compartments
  const [subCompartments, setSubCompartments] = useState([]);

  //convert to contract modal
  const [openConvertToContractModal, setOpenConvertToContractModal] = useState(false);

  //dropDownOptions
  const [allReqDropDown, setAllRequiredDropDown] = useState({
    species: [],
    destinations: [],
    products: [],
    customer: [],
  });

  //new info add
  const [openNewInfoAddDialog, setOpenNewInfoAddDialog] = useState(false);
  const [defaultValueNewInfoDialog, setDefaultValueNewInfoDialog] = useState({});
  const [newSectionAddName, setNewSectionAddName] = useState('');

  //draggable map view
  const [openRouteMapModal, setOpenRouteMapModal] = useState(false);
  const [allLocationDetails, setAllLocationDetails] = useState([]);

  const [savedRouteDetails, setSavedRouteDetails] = useState([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  // Quick Site Assessment
  const [quickAssessmentModal, setQuickAssessmentModal] = useState(false);
  const [step, setStep] = useState('ref');
  const [reference, setReference] = useState('');
  const [quickSiteID, setQuickSiteID] = useState(null);

  const [newSiteModal, setNewSiteModal] = useState(false);

  const [customerLocations, setCustomerLocation] = useState([]);

  const creatingRef = useRef(false);

  //confirmation dialog
  const {
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
  } = useConfirmationDialog();
  useEffect(() => {
    isMounted.current = true;
    getSiteAssessmentData();
    getReqSiteAssessmentDropdownOptions();
    getAllLocationData();
    return () => {
      isMounted.current = false; // Set to false on unmount
    };
  }, []);

  useEffect(() => {
    const getCustomer = async () => {
      try {
        const res = await customAxios.get(`/CRM/GetContacts?id=1`, { headers: authHeader() });
        const combination = res.data.map(item => ({
          label: item.crmContactFirst + item.crmContactMiddle + item.crmContactLast + (item.fellingLicenseNo || 0),
          value: item.crmId,
        }));
        setCustomerLocation(combination);
      } catch (err) {
        console.log('CRM Contacts error ', err);
      }
    };

    getCustomer();
  }, []);

  const getAllLocationData = async locationName => {
    let url = '/CRM/GetCustomerLocations';
    if (locationName) {
      url = '/CRM/GetCustomerLocations/' + locationName;
    }
    let response = await customAxios.get(url, { headers: authHeader() });
    if (response.status == 200) {
      if (locationName) {
        return response.data;
      } else {
        setAllLocationDetails(response.data);
      }
    }
  };

  // console.log(siteInfoData, 'xId');

  // useEffect(() => {
  //   console.log(siteInfoData, 'xId');
  // });

  const getCurrentSiteRouteDetails = async (siteId, shouldAddIfEmpty) => {
    try {
      // Fetch both sets of data in parallel
      const [allLocationsRes, routesRes] = await Promise.all([
        customAxios.get(`/CRM/GetCustomerLocations?siteAssesmentID=${siteId}`, {
          headers: authHeader(),
        }),
        customAxios.get(`/SiteDetails/GetSiteDetailsMapRoutes/${siteId}`, {
          headers: authHeader(),
        }),
      ]);

      const allLocations = allLocationsRes.data || [];
      const allRoutes = routesRes.data || [];

      // Create a map of routes by destination for quick lookup
      const routeMap = new Map(allRoutes.map(route => [route.destination, route]));

      // Merge allLocations with existing route data (if any)
      const mergedRoutes = allLocations.map(loc => {
        const route = routeMap.get(loc.destinationName);
        return {
          siteAssessmentID: siteId,
          destination: loc.destinationName,
          destinationLatitude: loc.latitude,
          destinationLongitude: loc.longitude,
          entranceLatitude: route?.entranceLatitude || null,
          entranceLongitude: route?.entranceLongitude || null,
          entrance: route?.entrance || '',
          polyLine: route?.polyLine || '',
          distance: route?.distance || null,
          ...(route?.id ? { id: route.id } : {}),
        };
      });

      // Save merged data to state
      setSavedRouteDetails(mergedRoutes);

      // If "shouldAddIfEmpty" is true AND no routes exist in backend,
      // we create new ones for each location.
      if (shouldAddIfEmpty && allRoutes.length === 0) {
        console.log('Adding new route entries for all locations...');
        for (const loc of allLocations) {
          if (loc.destinationName === 'Other') continue;

          const newRoute = {
            siteAssessmentID: siteId,
            entranceLatitude: null,
            entranceLongitude: null,
            destination: loc.destinationName,
            destinationLatitude: loc.latitude,
            destinationLongitude: loc.longitude,
            polyLine: '',
            distance: null,
            entrance: '',
          };

          await addNewRoute(newRoute);
        }

        // Re-fetch after adding
        await getCurrentSiteRouteDetails(siteId, false);
      }
    } catch (ex) {
      console.error('Error fetching route details:', ex);
    }
  };

  const addNewRoute = async routeObj => {
    const response = await customAxios.post('/SiteDetails/AddSiteDetailsMapRoutes', routeObj, {
      headers: authHeader(),
    });
    return response.status;
  };

  // Main site code
  // const getSiteAssessmentData = async () => {
  //   try {
  //     dispatch({ type: FETCH_START });
  //     customAxios.get('/CRM/GetCRMSiteDetails', { headers: authHeader() }).then(async response => {
  //       let correctedData = response.data;

  //       const data = await Promise.all(
  //         correctedData?.map(async item => {
  //           try {
  //             if (!item.crmID) return item;
  //             const extra = await customAxios.get(`/CRM/GetContacts?id=1&crmId=${item.crmID}`, { headers: authHeader() });
  //             return {
  //               ...item,
  //               ownerName: extra.data[0].crmContactFirst,
  //               address1: extra.data[0].crmAddressLine1,
  //               address2: extra.data[0].crmAddressCity,
  //             };
  //           } catch (err) {
  //             console.log('error in getting contact by crm id', err);
  //             return item;
  //           }
  //         }),
  //       );

  //       if (isMounted.current) {
  //         setSiteAssessmentData(data);
  //         dispatch({ type: FETCH_SUCCESS });
  //       }
  //       // setSiteAssessmentData(correctedData);
  //       // dispatch({ type: FETCH_SUCCESS });
  //     });
  //   } catch (error) {
  //     if (isMounted.current) {
  //       setSiteAssessmentData([]);
  //       console.error('error', error);
  //       dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
  //     }
  //   }
  // };

  const getSiteAssessmentData = useCallback(async () => {
    dispatch({ type: FETCH_START });

    try {
      const response = await customAxios.get('/CRM/GetCRMSiteDetails', { headers: authHeader() });

      const correctedData = response.data || [];

      const data = await Promise.all(
        correctedData.map(async item => {
          try {
            // if (!item.crmID) return item;

            // const extra = await customAxios.get(`/CRM/GetContacts?id=1&crmId=${item.crmID}`, { headers: authHeader() });

            return {
              ...item,
              // ownerName: extra.data?.[0]?.crmContactFirst,
              // address1: extra.data?.[0]?.crmAddressLine1,
              // address2: extra.data?.[0]?.crmAddressCity,
            };
          } catch (err) {
            console.log('error in getting contact by crm id', err);
            return item;
          }
        }),
      );

      if (isMounted.current) {
        setSiteAssessmentData(data);
        dispatch({ type: FETCH_SUCCESS });
      }
    } catch (error) {
      if (isMounted.current) {
        setSiteAssessmentData([]);
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
      console.error(error);
    }
  }, [dispatch]);

  // Without calling customer location
  // const getSiteAssessmentData = async () => {
  //   try {
  //     dispatch({ type: FETCH_START });

  //     const response = await customAxios.get('/CRM/GetCRMSiteDetails', { headers: authHeader() });

  //     const data = Array.isArray(response.data) ? response.data : [];

  //     if (isMounted.current) {
  //       setSiteAssessmentData(data);
  //       dispatch({ type: FETCH_SUCCESS });
  //     }
  //   } catch (error) {
  //     if (isMounted.current) {
  //       setSiteAssessmentData([]);
  //       dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
  //     }
  //     console.error('error', error);
  //   }
  // };

  const getSiteInfo = async siteData => {
    try {
      dispatch({ type: FETCH_START });

      setSelectedSiteData(siteData);
      setNotes({
        siteNotesforOperatorsOnly: siteData?.siteNotesforOperatorsOnly,
        siteNotesforHaulageOnly: siteData?.siteNotesforHaulageOnly,
        siteNotesforForestManagerOnly: siteData?.siteNotesforForestManagerOnly,
        additionalManagementNotes: siteData?.additionalManagementNotes,
      });

      const siteOptionsUrl = `/SiteDetails/GetSiteOptions/${siteData.siteAssessmentID}/?mapType=pin`;
      const crmSiteUrl = `/CRM/GetCRMSiteDetails?siteAssessmentId=${siteData.siteAssessmentID}`;

      // Fire both requests in parallel
      const [optionsResponse, crmSiteResponse] = await Promise.all([
        customAxios.get(siteOptionsUrl, { headers: authHeader() }),
        customAxios.get(crmSiteUrl, { headers: authHeader() }),
      ]);

      if (optionsResponse.status === 200 && crmSiteResponse.status === 200) {
        fetchMapCoordiates(siteData);
        fetchSamplesData(siteData);
        fetchSamples(siteData);
        fetchSubCompartmentSampleData(siteData);
        getCurrentSiteRouteDetails(siteData.siteAssessmentID, true);
        fetchDocs(siteData.siteAssessmentID);

        const currData = optionsResponse.data;
        const crmSite = crmSiteResponse.data;

        const volumeData = {
          averageTree: crmSite[0].averageTree,
          treesPerHectare: crmSite[0].treesPerHectare,
          percentOfForestCovered: crmSite[0].percentOfForestCovered,
          siteVolAreaInHa: crmSite[0].siteVolAreaInHa,
          totalVolume: crmSite[0].totalVolume,
          totalTons: crmSite[0].totalTons,
          totalTonsPerHa: crmSite[0].totalTonsPerHa,
          totalVolPerHa: crmSite[0].totalVolPerHa,
        };
        dispatch({ type: FETCH_SUCCESS });
        setSiteInfoData({
          ...currData,
          siteVolume: [volumeData],
          _lastUpdated: Date.now(), // 👈 trigger rerender via key
        });
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }

      setOpenSiteAssessmentDialog(true);
    } catch (err) {
      console.log(err);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  // const getSiteInfo = async siteData => {
  //   dispatch({ type: FETCH_START });
  //   setSelectedSiteData(siteData);
  //   setOpenSiteAssessmentDialog(true);
  //   let url = `/SiteDetails/GetSiteOptions/${siteData.siteAssessmentID}/?mapType=pin`;
  //   const response = await customAxios.get(url, {
  //     headers: authHeader(),
  //   });
  //   if (response.status === 200) {
  //     fetchMapCoordiates(siteData);
  //     fetchSamples(siteData);
  //     fetchSamplesData(siteData);
  //     console.log('Site Info: ', response.data);
  //     let currData = response.data;
  //     getCurrentSiteRouteDetails(siteData.siteAssessmentID, true);
  //     fetchDocs(siteData.siteAssessmentID);
  //     dispatch({ type: FETCH_SUCCESS });
  //     setSiteInfoData({ ...currData });
  //   } else {
  //     dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
  //   }
  // };

  const fetchDocs = async siteId => {
    try {
      setIsLoading(true);
      dispatch({ type: FETCH_START });

      const response = await customAxios.get(`/CRM/GetCRMSiteDetailsDocs?siteAssessmentID=${siteId}`, {
        headers: authHeader(),
      });

      if (response.status === 200 && response.data.length > 0) {
        const finalResponse = await Promise.all(
          response.data.map(item =>
            customAxios.get(`/CRM/GetCRMSiteDetailsFile?id=${item.id}`, {
              headers: authHeader(),
              responseType: 'blob',
            }),
          ),
        );

        const imageUrls = finalResponse.map((fileRes, idx) => {
          const urlObj = URL.createObjectURL(fileRes.data);
          const type = fileRes.data.type;
          console.log('response ====', response);
          return {
            id: response.data[idx].id,
            documentName: response.data[idx].fileAttachmentName,
            urlObj,
            type,
          };
        });

        setFiles(imageUrls);
        dispatch({ type: FETCH_SUCCESS });
      } else {
        setFiles([]);
        dispatch({ type: FETCH_SUCCESS });
      }
    } catch (err) {
      console.log('err ====', err);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    } finally {
      setIsLoading(false);
    }
  };

  const showFiles = () => setOpenFilesDialog(true);
  const closeFiles = () => setOpenFilesDialog(false);

  const handleFileChange = key => async file => {
    const fileData = file?.[0];

    const formData = new FormData();
    formData.append('siteAssessmentID', selectedSiteData.siteAssessmentID);
    formData.append('files', fileData);

    try {
      const response = await customFormAxios.post(`/CRM/AddCRMSiteDetailsDocs`, formData, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        showToast('File uploaded successfully', 'success');
        fetchDocs(selectedSiteData.siteAssessmentID);
      }
    } catch (err) {
      console.log(err);
      showToast('Something went wrong', 'error');
    }
  };

  const handleDeleteFiles = fileDataToDelete => {
    console.log(fileDataToDelete);

    if (window.confirm('Are you sure you want to delete this Document?')) {
      dispatch({ type: FETCH_START });
      let url = 'CRM/DeleteCRMSiteDetailsDocs';
      customAxios
        .post(url, { id: fileDataToDelete.id }, { headers: authHeader() })
        .then(data => {
          if (data.status == 200) {
            showToast('File deleted successfully.', 'success');
            fetchDocs(selectedSiteData.siteAssessmentID);
          }
          dispatch({ type: FETCH_SUCCESS });
        })
        .catch(error => {
          dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        });
    }
  };

  const viewFile = fileData => {
    const url = fileData.urlObj;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileData.documentName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const fetchMapCoordiates = async siteData => {
    const mapTypes = ['pin', 'polygon', 'line'];

    const responseArrays = await Promise.all(
      mapTypes.map(async item => {
        const response = await customAxios.get(
          `/SiteDetails/GetSiteDetailsMapCoordinates/${siteData.siteAssessmentID}?mapType=${item}`,
          { headers: authHeader() },
        );
        return response.data;
      }),
    );

    let consolidatedArray = responseArrays.flat();

    // 🛑 HARD FIX: Remove invalid polygon/line points
    consolidatedArray = consolidatedArray.filter(item => {
      // Remove rows missing lat/lng entirely
      if (!item.latitude || !item.longitude) return false;

      return true;
    });

    // 🛑 GROUP BY LABEL → FIX invalid polygons/lines
    const grouped = {};
    consolidatedArray.forEach(item => {
      if (!item.mapLabel) return;

      if (!grouped[item.mapLabel]) grouped[item.mapLabel] = [];
      grouped[item.mapLabel].push(item);
    });

    // 🛑 CLEANUP: Remove polygon/line groups with < 3 points (for polygon) or < 2 (for line)
    const cleaned = Object.values(grouped).flatMap(group => {
      const mapType = group[0]?.mapType;

      if (mapType === 'polygon' && group.length < 3) {
        console.warn('Removed invalid polygon:', group[0].mapLabel);
        return []; // remove whole polygon
      }

      if (mapType === 'line' && group.length < 2) {
        console.warn('Removed invalid line:', group[0].mapLabel);
        return [];
      }

      return group;
    });

    // UNIQUE SUB COMPARTMENT ARR
    const subCompartmentsArr = Array.from(
      new Map(cleaned?.filter(item => item.mapLabel?.startsWith('Sub Comp')).map(item => [item.mapLabel, item])).values(),
    );

    setSubCompartments(subCompartmentsArr);
    setMapData(cleaned);
  };

  // Handle Save Map Coordinates
  const saveMapCoordinates = async (feature, mapType, originalLabel) => {
    console.log('FEATURE ====', feature);
    const { mapLabel, displayName, hexColor, area, length } = feature;
    const { siteAssessmentID } = selectedSiteData;

    // Match all backend items with same label and type
    const backendItems = mapData.filter(item => item.mapType === mapType && item.mapLabel === originalLabel);

    const url =
      backendItems.length > 0 ? `/SiteDetails/EditSiteDetailsMapCoordinates` : `/SiteDetails/AddSiteDetailsMapCoordinates`;

    try {
      if (mapType === 'pin') {
        const payload = {
          id: backendItems[0]?.id ?? undefined, // first match if any
          siteAssessmentID,
          mapLabel,
          displayName,
          hexColor,
          latitude: feature.latitude,
          longitude: feature.longitude,
          mapType,
          relatedTo: feature.relatedTo,
          area: 0,
        };

        const response = await customAxios.post(url, [payload], {
          headers: authHeader(),
        });

        if (response.status === 200) {
          fetchMapCoordiates(selectedSiteData);
        }
      } else {
        // Multiple coords — use IDs if available
        let areaValue = 0;
        if (mapType === 'polygon') {
          areaValue = calculatePolygonAreaGoogle(feature.coords);
        } else {
          areaValue = length;
        }
        const payloads = feature.coords.map((coord, idx) => ({
          id: backendItems[idx]?.id ?? undefined,
          siteAssessmentID,
          mapLabel,
          displayName,
          hexColor,
          sortOrder: idx,
          latitude: coord.lat,
          longitude: coord.lng,
          mapType,
          area: areaValue,
        }));

        const response = await customAxios.post(url, payloads, {
          headers: authHeader(),
        });

        if (response.status === 200) {
          await updateRecordModifiedDate(siteAssessmentID);
          fetchMapCoordiates(selectedSiteData);
        }
      }
    } catch (err) {
      console.error('Map Save Error:', err);
    }
  };

  const updateMapCoordinates = async (feature, mapType) => {
    const { siteAssessmentID } = selectedSiteData;
    const { mapLabel, hexColor, coords, length } = feature;

    // Match backend items by label + type to get their IDs
    const backendItems = mapData.filter(item => item.mapType === mapType && item.mapLabel === mapLabel);

    let areaValue = 0;
    if (mapType === 'polygon') {
      areaValue = calculatePolygonAreaGoogle(coords);
    } else if (mapType === 'line') {
      areaValue = length;
    }

    if (mapType === 'pin') {
      const payload = [
        {
          id: backendItems[0]?.id ?? undefined,
          siteAssessmentID,
          mapLabel,
          displayName: feature.displayName,
          hexColor,
          latitude: feature.latitude,
          longitude: feature.longitude,
          mapType,
          relatedTo: feature.relatedTo,
          area: 0,
        },
      ];

      try {
        await customAxios.post(`/SiteDetails/EditSiteDetailsMapCoordinates`, payload, {
          headers: authHeader(),
        });

        await updateRecordModifiedDate(siteAssessmentID);
      } catch (err) {
        console.error('Map coord update error:', err);
      }
      return;
    }

    const payloads = coords.map((coord, idx) => ({
      id: backendItems[idx]?.id ?? undefined,
      siteAssessmentID,
      mapLabel,
      displayName: feature.displayName,
      hexColor,
      sortOrder: idx,
      latitude: coord.lat,
      longitude: coord.lng,
      mapType,
      area: areaValue,
    }));

    try {
      await customAxios.post(`/SiteDetails/EditSiteDetailsMapCoordinates`, payloads, {
        headers: authHeader(),
      });

      await updateRecordModifiedDate(siteAssessmentID);
    } catch (err) {
      console.error('Map coord update error:', err);
    }
  };

  // Handle Map Delete
  const deleteMapCoordinates = async id => {
    const { siteAssessmentID } = selectedSiteData;
    const payload = {
      id,
      siteAssessmentID,
    };

    const response = await customAxios.post(`/SiteDetails/DeleteSiteDetailsMapCoordinates`, payload, {
      headers: authHeader(),
    });

    if (response.status == 200) {
      fetchMapCoordiates(selectedSiteData);
    }
  };

  // Samples
  const fetchSamples = async ({ siteAssessmentID }) => {
    dispatch({ type: FETCH_START });
    const url = `/SiteDetails/GetSiteDetailsSamples/${siteAssessmentID}`;
    const response = await customAxios.get(url, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      // GET AREA IN HA
      const samplesData = await customAxios.get(`/SiteDetails/GetSiteDetailsSamplesData/${siteAssessmentID}`, {
        headers: authHeader(),
      });
      const areaMap = {};
      samplesData.data.forEach(item => {
        if (item.subCompartmentName) {
          areaMap[item.subCompartmentName] = parseFloat(item.areaInHa || 0);
        }
      });
      const speciesDetails = calculateSpeciesData(response.data, areaMap);
      setSamples(response.data);
      setSiteInfoData(prev => ({
        ...prev,
        species: speciesDetails,
      }));
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const calculateSpeciesData = (samplesData, areaMap = {}) => {
    if (!Array.isArray(samplesData)) return [];

    const filteredSamples = samplesData.filter(item => item.takeOut === 'Yes');
    const groupMap = {};

    filteredSamples.forEach(sample => {
      const groupKey = `${sample.subCompartmentName}___${sample.sampleName}`;
      const speciesKey = sample.species;

      if (!groupMap[groupKey]) {
        groupMap[groupKey] = {};
      }
      if (!groupMap[groupKey][speciesKey]) {
        groupMap[groupKey][speciesKey] = {
          species: speciesKey,
          subCompartmentName: sample.subCompartmentName,
          sampleName: sample.sampleName,
          totalDbh: 0,
          areaInHa: parseFloat(areaMap[sample.subCompartmentName] || 0), // 👈 assign area
        };
      }

      groupMap[groupKey][speciesKey].totalDbh += sample.dbh;
    });

    const result = [];

    Object.values(groupMap).forEach(speciesGroup => {
      const totalGroupDbh = Object.values(speciesGroup).reduce((sum, item) => sum + item.totalDbh, 0);

      Object.values(speciesGroup).forEach(item => {
        result.push({
          ...item,
          percentageOfSite: totalGroupDbh > 0 ? parseFloat(((item.totalDbh / totalGroupDbh) * 100).toFixed(2)) : 0,
        });
      });
    });

    return result;
  };

  const fetchSamplesData = async ({ siteAssessmentID }) => {
    dispatch({ type: FETCH_START });
    const url = `/SiteDetails/GetSiteDetailsSamplesData/${siteAssessmentID}`;
    const response = await customAxios.get(url, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      console.log('response.data ===', response.data);
      setSamplesData(response.data);
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const fetchSubCompartmentSampleData = async ({ siteAssessmentID }) => {
    dispatch({ type: FETCH_START });
    const url = `/SiteDetails/GetSiteDetailsSubCompartmentSamples/${siteAssessmentID}`;
    const response = await customAxios.get(url, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      console.log('response.data ===', response.data);
      setSubCompSampleData(response.data);
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleSubCompSampleData = async (data, type) => {
    const { siteAssessmentID } = selectedSiteData;
    const url =
      type === 'add'
        ? '/SiteDetails/AddSiteDetailsSubCompartmentSamples/'
        : '/SiteDetails/EditSiteDetailsSubCompartmentSamples/';

    const payload = { ...data, siteAssessmentID };

    const response = await customAxios.post(url, payload, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      fetchSubCompartmentSampleData({ siteAssessmentID });
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleAction = async (data, type) => {
    let url;
    let response;
    const { siteAssessmentID } = selectedSiteData;

    dispatch({ type: FETCH_START });
    switch (type) {
      case 'add':
        const { id, ...payload } = data;
        const finalPayload = [{ ...payload, siteAssessmentID }];
        url = `/SiteDetails/AddSiteDetailsSamples/`;
        response = await customAxios.post(url, finalPayload, {
          headers: authHeader(),
        });
        break;

      case 'update':
        const updatePayload = { ...data, siteAssessmentID };
        url = `/SiteDetails/EditSiteDetailsSamples/`;
        response = await customAxios.post(url, updatePayload, {
          headers: authHeader(),
        });
        break;

      case 'delete':
        const delPayload = { id: data.id };
        url = `/SiteDetails/DeleteSiteDetailsSamples/`;
        response = await customAxios.post(url, delPayload, {
          headers: authHeader(),
        });
        break;
    }

    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      fetchSamples({ siteAssessmentID });
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleOpenSiteInfoEditModal = (sectionName, sectionData) => {
    setOpenSectionEditModal(true);
    console.log(sectionData, sectionName);
    setSelectedSectionData({
      sectionData: sectionData,
      sectionName: sectionName,
    });
  };
  const handleCloseSiteInfoEditModal = () => {
    setOpenSectionEditModal(false);

    setSelectedSectionData(NOSECTION_SELECT);
  };

  const normalizeToNumbers = obj =>
    Object.fromEntries(Object.entries(obj).map(([key, val]) => [key, isNaN(val) ? val : Number(val)]));

  const handleSiteAssementSectionEditSave = async (sectionName, editedData) => {
    console.log('Edited Data:', editedData);
    dispatch({ type: FETCH_START });

    let url = '';
    let payload = editedData;
    let axiosClient = customAxios;

    try {
      switch (sectionName) {
        case 'checklist':
          url = '/SiteDetails/EditSiteDetailsChecklist';
          break;
        case 'info':
          url = '/SiteDetails/EditSiteDetailsInfo';
          break;
        case 'geolocations':
          url = '/SiteDetails/EditSiteDetailsGeolocations';
          break;
        case 'harvesting':
          url = '/SiteDetails/EditSiteDetailsHarvesting';
          break;
        case 'hazards':
          url = '/SiteDetails/EditSiteDetailsHazards';
          break;
        case 'products':
          url = '/SiteDetails/EditSiteDetailsProducts';
          break;
        case 'species':
          url = '/SiteDetails/EditSiteDetailsSpecies';
          break;
        case 'siteVolume':
          url = '/CRM/EditCRMSiteDetails';
          axiosClient = customFormAxios;

          const normalizedSiteVol = normalizeToNumbers(editedData);
          const crmRes = await customAxios.get(
            `/CRM/GetCRMSiteDetails?siteAssessmentId=${selectedSiteData?.siteAssessmentID}`,
            { headers: authHeader() },
          );

          payload = {
            ...crmRes?.data?.[0],
            ...normalizedSiteVol,
          };

          // Convert to FormData
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            formData.append(key, value);
          });

          payload = formData;
          break;
        default:
          throw new Error(`Unsupported section: ${sectionName}`);
      }

      const response = await axiosClient.post(url, payload, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        dispatch({ type: FETCH_SUCCESS });
        showToast('Data updated successfully', 'success');
        getSiteInfo(selectedSiteData);
        handleCloseSiteInfoEditModal();
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        showToast('Failed to save data', 'error');
      }

      await updateRecordModifiedDate(selectedSiteData?.siteAssessmentID);
    } catch (error) {
      console.error('Error saving site assessment section:', error);
      dispatch({ type: FETCH_ERROR, payload: 'Unexpected Error' });
      showToast('Failed to save data', 'error');
    }
  };

  const showSiteMap = async () => {
    setOpenGoogleMapView(true);
  };
  //convert to contract operations
  const hadnleOpenConvertToContractModal = toConvertSiteAssessment => {
    setSelectedSiteData(toConvertSiteAssessment);
    setOpenConvertToContractModal(true);
  };

  const displayAPIError = error => {
    dispatch({ type: FETCH_ERROR, payload: error || 'Network Error' });
  };

  const deleteProductData = (productId, siteAssesmentId, isConfirm) => {
    try {
      if (!isConfirm) {
        handleOpenConfirmationDialog(
          'Do You wish to delete the selected product ?',
          () => deleteProductData(productId, siteAssesmentId, true),
          null,
        );
        return;
      }
      dispatch({ type: FETCH_START });
      let obj = {
        siteAssessmentID: siteAssesmentId,
        id: productId,
      };
      customAxios.post('/SiteDetails/DeleteSiteDetailsProducts', obj, { headers: authHeader() }).then(res => {
        dispatch({ type: FETCH_SUCCESS });
        if (res.status == 200) {
          getSiteInfo(selectedSiteData);
          showToast('Product deleted successfully!', 'success');
        } else {
          showToast('Network Error!', 'error');
        }
      });
    } catch (ex) {
      showToast('Network Error!', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      console.log(ex);
    }
  };
  const deleteSpecies = (speciesId, siteAssesmentId, isConfirm) => {
    try {
      if (!isConfirm) {
        handleOpenConfirmationDialog(
          'Do You wish to delete the selected species ?',
          () => deleteSpecies(speciesId, siteAssesmentId, true),
          null,
        );
        return;
      }
      dispatch({ type: FETCH_START });
      let obj = {
        siteAssessmentID: siteAssesmentId,
        id: speciesId,
      };

      customAxios.post('/SiteDetails/DeleteSiteDetailsSpecies', obj, { headers: authHeader() }).then(res => {
        dispatch({ type: FETCH_SUCCESS });
        if (res.status == 200) {
          getSiteInfo(selectedSiteData);
          showToast('Species deleted successfully!', 'success');
        } else {
          showToast('Network Error!', 'error');
        }
      });
    } catch (ex) {
      showToast('Network Error!', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      console.log(ex);
    }
  };
  const handleContractSubmit = async (
    addContractData,
    certificationInfo,
    addContractCoillte,
    addContractInvoicingRates,
    addContractProductDetails,
    addContractRates,
    files,
  ) => {
    try {
      dispatch({ type: FETCH_START });
      let contractId = null;
      const addContractResponse = await customAxios
        .post('/Ops/AddContract', addContractData, { headers: authHeader() })
        .catch(error => {
          handleOpenConfirmationDialog('Error : Contract cannot be created !', null, null);
        });
      if (addContractResponse.status != 200) {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
        return;
      }
      contractId = addContractResponse.data.newContractUniqueID;

      //file upload section start
      let UploadContractDocsUrl = '/Ops/AddContractsDocs';

      if (files.office) {
        let formData = new FormData();
        formData.append('contractUniqueID', contractId);
        formData.append('docType', 'officeDocs');
        for (let i = 0; i < files.office.length; i++) {
          formData.append(`file[${i}]`, files.office[i]);
        }
        const officeFileUploadRes = await customAxios
          .post(UploadContractDocsUrl, formData, { headers: authHeader() })
          .catch(error => {
            displayAPIError('Failed to upload office files');
          });
      }
      if (files.operator) {
        let formData = new FormData();
        formData.append('contractUniqueID', contractId);
        formData.append('docType', 'operatorDocs');
        for (let i = 0; i < files.operator.length; i++) {
          formData.append(`file[${i}]`, files.operator[i]);
        }
        const operatorFileUploadRes = await customAxios
          .post(UploadContractDocsUrl, formData, { headers: authHeader() })
          .catch(error => {
            displayAPIError('Failed to upload operator files');
          });
      }
      //file upload section end

      let AddContractCertificationInfoUrl = '/Ops/AddContractCertificationInfo';
      certificationInfo['contractUniqueID'] = contractId;
      const certificationInfoUploadUrl = await customAxios
        .post(AddContractCertificationInfoUrl, certificationInfo, {
          headers: authHeader(),
        })
        .catch(error => {
          displayAPIError('Failed to upload Contract Certification Info');
        });

      const AddContractCoillteUrl = '/Ops/AddContractCoillte';
      addContractCoillte['contractUniqueID'] = contractId;
      const addContractColliteRes = await customAxios
        .post(AddContractCoillteUrl, addContractCoillte, {
          headers: authHeader(),
        })
        .catch(error => {
          displayAPIError('Failed to upload Contract Coillte');
        });

      const AddContractInvoicingRatesUrl = '/Ops/AddContractInvoicingRates';
      addContractInvoicingRates['contractUniqueID'] = contractId;
      const addContractInvoicingRatesRes = await customAxios
        .post(AddContractInvoicingRatesUrl, addContractInvoicingRates, {
          headers: authHeader(),
        })
        .catch(error => {
          displayAPIError('Failed to upload Contract Invoicing Rates');
        });

      const AddContractProductDetailsUrl = '/Ops/AddContractProductDetails';
      addContractProductDetails['contractUniqueID'] = contractId;
      const addContractProductDetailsRes = await customAxios
        .post(AddContractProductDetailsUrl, addContractProductDetails, {
          headers: authHeader(),
        })
        .catch(error => {
          displayAPIError('Failed to upload Contract Product Details');
        });

      const AddContractRatesUrl = '/Ops/AddContractRates';
      addContractRates['contractUniqueID'] = contractId;
      const addContractRatesRes = await customAxios
        .post(AddContractRatesUrl, addContractRates, {
          headers: authHeader(),
        })
        .catch(error => {
          displayAPIError('Failed to upload Contract Rates');
        });

      dispatch({ type: FETCH_SUCCESS });
      setOpenConvertToContractModal(false);
      handleOpenConfirmationDialog('Success : Contract created successfully !', null, null);
    } catch (ex) {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      console.log(ex);
    }
  };

  const getReqSiteAssessmentDropdownOptions = async () => {
    let reqDropDown = ['Species'];
    let outputArr = {
      species: [],
      destinations: [],
      products: [],
      customer: [],
    };

    await customAxios
      .post('/GenTasks/GetDropdownListArray', reqDropDown, { headers: authHeader() })
      .then(res => {
        if (res.status == 200) {
          console.log(res.data);
          let allData = res.data;
          for (let i = 0; i < allData.length; i++) {
            let keyName = allData[i]['keyName'].toLowerCase();
            outputArr[keyName] = allData[i]['values'];
          }
        }
      })
      .catch(Ex => {
        console.log(Ex);
      });

    await customAxios.get('/CRM/GetAllCustomerLocations', { headers: authHeader() }).then(res => {
      if (res.status === 200) {
        const destinationNames = res.data.map(item => item.destinationName);
        outputArr['destinations'] = destinationNames;
      }
    });

    await customAxios
      .get('/Ops/GetProducts', { headers: authHeader() })
      .then(res => {
        if (res.status == 200) {
          let allData = res.data;
          let reqVals = [];
          for (let i = 0; i < allData.length; i++) {
            reqVals.push(allData[i]['productName']);
          }
          outputArr['products'] = reqVals;
        }
      })
      .catch(ex => {
        console.log(ex);
      });

    await customAxios
      .get('/CRM/GetSageCustomers', { headers: authHeader() })
      .then(res => {
        if (res.status === 200) {
          outputArr['customer'] = res.data.map(item => item.customerName);
        }
      })
      .catch(ex => {
        console.log(ex);
      });

    setAllRequiredDropDown(outputArr);
  };

  const handleNewInfoSave = (newData, sectionName) => {
    try {
      console.log(newData, sectionName);
      dispatch({ type: FETCH_START });

      let addUrl = '/SiteDetails/AddSiteDetailsProducts';
      if (sectionName == 'species') {
        addUrl = '/SiteDetails/AddSiteDetailsSpecies';
      }

      const dataObj = [{ ...newData, siteAssessmentID: selectedSiteData.siteAssessmentID }];
      customAxios
        .post(addUrl, dataObj, { headers: authHeader() })
        .then(res => {
          if (res.status == 200) {
            let newSectionName = sectionName.charAt(0).toUpperCase() + (sectionName.length >= 2 && sectionName.slice(1));
            showToast(`New ${newSectionName} added successfully `, 'success');
            getSiteInfo(selectedSiteData);
            setOpenNewInfoAddDialog(false);
          }
          dispatch({ type: FETCH_SUCCESS });
        })
        .catch(ex => {
          dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
          console.log(ex);
        });
    } catch (ex) {
      console.log(ex);
    }
  };

  const handleNewSectionAddClick = sectionName => {
    let defaultVal = {};
    if (sectionName == 'products') {
      defaultVal = { ...DEFAULT_PRODUCT_DATA_VALUE };
    } else if (sectionName == 'species') {
      defaultVal = { ...DEFAULT_SPECIES_DATA_VALUE };
    }
    setNewSectionAddName(sectionName);
    setDefaultValueNewInfoDialog(defaultVal);
    setOpenNewInfoAddDialog(true);

    // console.log('sectinNAme', sectionName, defaultVal);
  };

  const handleSiteNotes = (key, value) => {
    setNotes(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const appendIfValid = (formData, key, value) => {
    if (value !== null && value !== undefined) {
      formData.append(key, value);
    }
  };

  // Saving site notes
  const saveSiteNotes = async (siteMainData, siteRef, siteCRMId) => {
    console.log(siteCRMId, siteRef, 'xyz');

    try {
      dispatch({ type: FETCH_START });

      // 1. Clean existing selected data
      const cleanedSiteData = Object.fromEntries(
        Object.entries(selectedSiteData || {}).filter(([key, value]) => {
          const invalidKeys = ['index', 'column'];
          return !invalidKeys.includes(key) && value !== null && value !== undefined && value !== '';
        }),
      );

      // 2. Build payload for comparison
      const payload = {
        ...cleanedSiteData,
        siteNotesforOperatorsOnly: notes?.siteNotesforOperatorsOnly,
        siteNotesforHaulageOnly: notes?.siteNotesforHaulageOnly,
        siteNotesforForestManagerOnly: notes?.siteNotesforForestManagerOnly,
        additionalManagementNotes: notes?.additionalManagementNotes,
      };

      // 3. Build current API data snapshot (from siteMainData)
      const currentSnapshot = {
        siteRef: siteMainData.siteDetailsRef,
        crmID: siteMainData.crmID,
        siteNotesforOperatorsOnly: siteMainData.siteNotesforOperatorsOnly,
        siteNotesforHaulageOnly: siteMainData.siteNotesforHaulageOnly,
        siteNotesforForestManagerOnly: siteMainData.siteNotesforForestManagerOnly,
        additionalManagementNotes: siteMainData.additionalManagementNotes,
        ...cleanedSiteData,
      };

      // 4. If nothing has changed → skip API call

      const nothingChanged =
        siteRef === siteMainData.siteDetailsRef &&
        siteCRMId === siteMainData.crmID &&
        JSON.stringify(payload) === JSON.stringify(currentSnapshot);

      if (nothingChanged) {
        console.warn('No changes');
        dispatch({ type: FETCH_SUCCESS });
        return;
      }

      // 5. Build FormData
      const formData = new FormData();
      appendIfValid(formData, 'crmID', siteCRMId);
      appendIfValid(formData, 'submissionDate', siteMainData.submissionDate);
      appendIfValid(formData, 'siteAssessmentID', siteMainData.siteAssessmentID);
      appendIfValid(formData, 'ref', siteRef);
      appendIfValid(formData, 'wH_Number', siteMainData.wH_Number);
      appendIfValid(formData, 'growerPhoneNumber', siteMainData.growerPhoneNumber);
      appendIfValid(formData, 'licenseNo', siteMainData.licenseNo);
      appendIfValid(formData, 'licenseStatus', siteMainData.licenseStatus);
      appendIfValid(formData, 'harvestPlanLink', siteMainData.harvestPlanLink);
      appendIfValid(formData, 'vatRate', siteMainData.vatRate);
      appendIfValid(formData, 'treesPerHectare', siteMainData.treesPerHectare);
      appendIfValid(formData, 'averageTree', siteMainData.averageTree);
      appendIfValid(formData, 'percentOfForestCovered', siteMainData.percentOfForestCovered);
      appendIfValid(formData, 'siteVolAreaInHa', siteMainData.siteVolAreaInHa);
      appendIfValid(formData, 'totalVolume', siteMainData.totalVolume);
      appendIfValid(formData, 'totalTons', siteMainData.totalTons);
      appendIfValid(formData, 'totalVolPerHa', siteMainData.totalVolPerHa);
      appendIfValid(formData, 'totalTonsPerHa', siteMainData.totalTonsPerHa);
      appendIfValid(formData, 'operatorEmail1', siteMainData.operatorEmail1);
      appendIfValid(formData, 'operatorEmail2', siteMainData.operatorEmail2);
      appendIfValid(formData, 'operatorEmail3', siteMainData.operatorEmail3);
      appendIfValid(formData, 'siteNotesforHaulageOnly', notes?.siteNotesforHaulageOnly);
      appendIfValid(formData, 'siteNotesforOperatorsOnly', notes?.siteNotesforOperatorsOnly);
      appendIfValid(formData, 'additionalManagementNotes', notes?.additionalManagementNotes);
      appendIfValid(formData, 'siteNotesforForestManagerOnly', notes?.siteNotesforForestManagerOnly);
      appendIfValid(formData, 'finaliseAssessment', siteMainData.finaliseAssessment);
      appendIfValid(formData, 'recordModifiedDate', siteMainData.recordModifiedDate);
      appendIfValid(formData, 'harvesterMachine', siteMainData.harvesterMachine);
      appendIfValid(formData, 'extractionMachine', siteMainData.extractionMachine);
      appendIfValid(formData, 'siteAssessmentType', siteMainData.siteAssessmentType);
      appendIfValid(formData, 'siteAssessmentCategory', siteMainData.siteAssessmentCategory);

      // setSelectedSiteData(selectedSiteData.)

      // 6. Fire API
      const response = await customFormAxios.post('/CRM/EditCRMSiteDetails', formData, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        dispatch({ type: FETCH_SUCCESS });
        getSiteAssessmentData();
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    } catch (err) {
      console.log(err);
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const openRouteView = () => {
    setOpenRouteMapModal(true);
  };

  async function saveSiteRef(siteMainData, siteRef, siteCRMId) {
    const formData = new FormData();

    formData.append('crmID', siteCRMId);
    formData.append('submissionDate', siteMainData.submissionDate);
    formData.append('siteAssessmentID', siteMainData.siteAssessmentID);
    formData.append('ref', siteRef);
    formData.append('wH_Number', siteMainData.wH_Number);
    formData.append('growerPhoneNumber', siteMainData.growerPhoneNumber);
    formData.append('licenseNo', siteMainData.licenseNo);
    formData.append('licenseStatus', siteMainData.licenseStatus);
    formData.append('harvestPlanLink', siteMainData.harvestPlanLink);
    formData.append('vatRate', siteMainData.vatRate);
    formData.append('treesPerHectare', siteMainData.treesPerHectare);
    formData.append('averageTree', siteMainData.averageTree);
    formData.append('percentOfForestCovered', siteMainData.percentOfForestCovered);
    formData.append('siteVolAreaInHa', siteMainData.siteVolAreaInHa);
    formData.append('totalVolume', siteMainData.totalVolume);
    formData.append('totalTons', siteMainData.totalTons);
    formData.append('totalVolPerHa', siteMainData.totalVolPerHa);
    formData.append('totalTonsPerHa', siteMainData.totalTonsPerHa);
    formData.append('operatorEmail1', siteMainData.operatorEmail1);
    formData.append('operatorEmail2', siteMainData.operatorEmail2);
    formData.append('operatorEmail3', siteMainData.operatorEmail3);
    formData.append('siteNotesforHaulageOnly', siteMainData.siteNotesforHaulageOnly);
    formData.append('siteNotesforOperatorsOnly', siteMainData.siteNotesforOperatorsOnly);
    formData.append('additionalManagementNotes', siteMainData.additionalManagementNotes);
    formData.append('finaliseAssessment', siteMainData.finaliseAssessment);
    formData.append('recordModifiedDate', siteMainData.recordModifiedDate);
    formData.append('harvesterMachine', siteMainData.harvesterMachine);
    formData.append('extractionMachine', siteMainData.extractionMachine);
    formData.append('siteNotesforForestManagerOnly', siteMainData.siteNotesforForestManagerOnly);
    formData.append('siteAssessmentType', siteMainData.siteAssessmentType);
    formData.append('siteAssessmentCategory', siteMainData.siteAssessmentCategory);

    await customAxios.post('/CRM/EditCRMSiteDetails', formData, {
      headers: {
        ...authHeader(),
        // 'Content-Type': 'multipart/form-data',
      },
    });

    await updateRecordModifiedDate(siteMainData.siteAssessmentID);
    getSiteAssessmentData();
  }

  function handleSiteDelete(siteId) {
    if (!siteId) {
      showToast(`No site found something went wrong `);

      return;
    }

    try {
      dispatch({ type: FETCH_START });
      const res = customAxios.post('/CRM/DeleteSite', { siteAssessmentID: siteId }, { headers: authHeader() });
      getSiteAssessmentData();
      showToast(`${siteId} Site ID deleted successfully`);
      setOpenDeleteModal(false);
    } catch (err) {
      console.log(err);
      setOpenDeleteModal(false);
    }
  }

  // QUICK SITE ASSESSMENT =======================
  // Open
  const showQuickAssessmentModal = () => {
    setQuickAssessmentModal(true);
  };

  const closeQuickAssessmentModal = () => {
    creatingRef.current = false;
    setReference('');
    setStep('ref');
    setQuickSiteID(null);
    setQuickAssessmentModal(false);
  };

  const handleContinue = async () => {
    if (!reference.trim()) return;

    // ✅ If site already created, just go to map
    if (quickSiteID) {
      console.log('Site already exists:', quickSiteID);
      setStep('map');
      return;
    }

    // ✅ Prevent duplicate API calls
    if (creatingRef.current) {
      console.log('Already creating...');
      return;
    }

    try {
      creatingRef.current = true;

      const formData = new FormData();
      formData.append('ref', reference);
      formData.append('siteAssessmentType', 'Customer Site');
      formData.append('submissionDate', toLocalISO());
      formData.append('siteAssesmentCategory', 'Customer');

      const res = await customAxios.post('/CRM/AddCRMSiteDetails', formData, { headers: authHeader() });

      if (res.status === 200) {
        console.log('SITE CREATED:', res.data);
        const siteId = res.data.newSiteAssessmentID;

        // Store the ID in separate state
        setQuickSiteID(siteId);
        setSelectedSiteData({ siteAssessmentID: siteId });

        setStep('map');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to create site assessment', 'error');
    } finally {
      creatingRef.current = false;
    }
  };

  const handleQuickAssessmentSave = async (type, polygon, customers) => {
    try {
      const centre = getPolygonCentroid(polygon[0]?.coords);

      if (type === 'customer') {
        const payload = {
          destinationName: customers?.destinationName,
          custCode: getCustCode(customers?.customerName, customers?.destinationName),
          latitude: String(centre?.lat),
          longitude: String(centre?.lng),
          siteAssessmentId: quickSiteID,
          customerName: customers?.customerName,
          isWoodchipDestination: 0,
        };

        await customAxios.post('/CRM/AddCustomerLocation', payload, { headers: authHeader() });
      }

      const geofencePayload = {
        siteAssessmentID: quickSiteID,
        contractUniqueID: '',
        locationName: reference,
        latitude: centre?.lat,
        longitude: centre?.lng,
        radius: 5000,
        mapType: 'polygon',
        geofenceType: type,
      };

      await customAxios.post('/Ops/AddGeofencingData', geofencePayload, { headers: authHeader() });

      // ✅ SUCCESS AFTER ALL CALLS
      showToast('Site Assessment Saved successfully', 'success');

      await updateRecordModifiedDate(quickSiteID);
      closeQuickAssessmentModal(); // close modal here
    } catch (error) {
      console.error(error);
      showToast('Failed to save', 'error');
    }
  };

  // Helper to find center of polygon
  const getPolygonCentroid = coords => {
    let area = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      const xi = coords[i].lng;
      const yi = coords[i].lat;
      const xj = coords[j].lng;
      const yj = coords[j].lat;

      const f = xi * yj - xj * yi;
      area += f;
      cx += (xi + xj) * f;
      cy += (yi + yj) * f;
    }

    area *= 0.5;

    return {
      lat: cy / (6 * area),
      lng: cx / (6 * area),
    };
  };

  // helper to generate custCode
  const getCustCode = (customerName = '', destinationName = '') => {
    const custWord = customerName
      .trim()
      .split(' ')[0]
      ?.toUpperCase();
    const destWord = destinationName
      .trim()
      .split(' ')[0]
      ?.toUpperCase();

    if (!custWord && !destWord) return '';
    if (custWord === destWord) return custWord;

    return `${custWord}${destWord}`;
  };

  // ADD NEW SITE ===========
  const showNewSiteModal = () => setNewSiteModal(true);
  const closeNewSiteModal = () => setNewSiteModal(false);

  // 🗺️ HANDLE MAP CLOSE - Create/Update Geofence
  const handleMapEditorClose = async mapState => {
    if (!selectedSiteData?.siteAssessmentID) return;

    try {
      let coords = null;
      let entrancePin = null;

      // Try to get entrance pin coordinates
      if (mapState?.pins?.length > 0) {
        entrancePin = mapState.pins.find(pin => pin.mapLabel?.startsWith('Entrance'));
        if (entrancePin) {
          coords = { lat: entrancePin.latitude, lng: entrancePin.longitude };
        }
      }

      // Fallback: use first pin if no entrance
      if (!coords && mapState?.pins?.length > 0) {
        const firstPin = mapState.pins[0];
        coords = { lat: firstPin.latitude, lng: firstPin.longitude };
      }

      // Fallback: use map center
      if (!coords && mapState?.center) {
        coords = mapState.center;
      }

      // Fallback: use default coordinates
      if (!coords) {
        coords = { lat: 53.41291, lng: -8.24389 };
      }

      // 🔍 Check if geofence already exists for this site
      let existingGeofence = null;
      try {
        const geofenceCheckRes = await customAxios.get(
          `/Ops/GetGeofencingData?siteAssessmentID=${selectedSiteData.siteAssessmentID}`,
          { headers: authHeader() },
        );
        existingGeofence = geofenceCheckRes?.data?.[0];
      } catch (err) {
        console.warn('Could not fetch existing geofence:', err);
      }

      const geoFencePayload = {
        siteAssessmentID: selectedSiteData.siteAssessmentID,
        contractUniqueID: '',
        locationName: selectedSiteData.siteDetailsRef,
        latitude: coords.lat,
        longitude: coords.lng,
        radius: 500000,
        mapType: 'pin',
        geofenceType: 'site',
      };

      const hasRealCoords = Boolean((mapState?.pins?.length > 0 && (entrancePin || mapState.pins[0])) || mapState?.center);

      // 🔄 UPDATE if exists, CREATE if not
      if (existingGeofence?.id) {
        if (!hasRealCoords) {
          console.warn('Skipping existing geofence update because no real coordinates were available');
        } else {
          await customAxios.post(
            '/Ops/EditGeofencingData',
            { ...geoFencePayload, id: existingGeofence.id },
            { headers: authHeader() },
          );
          console.log('✅ Geofence UPDATED with actual map coordinates:', coords);
        }
      } else {
        await customAxios.post('/Ops/AddGeofencingData', geoFencePayload, {
          headers: authHeader(),
        });
        console.log('✅ Geofence CREATED with actual map coordinates:', coords);
      }
    } catch (error) {
      console.error('⚠️ Failed to create/update geofence on map close:', error);
    }
  };

  return {
    siteAssessmentData,
    getSiteInfo,
    openSiteAssessmentDialog,
    setOpenSiteAssessmentDialog,
    siteInfoData,
    setSiteInfoData,
    selectedSiteData,
    setSelectedSiteData,
    openSectionEditModal,
    setOpenSectionEditModal,
    handleOpenSiteInfoEditModal,
    selectionSectionData,
    setSelectedSectionData,
    handleSiteAssementSectionEditSave,
    mapData,
    openGoogleMapView,
    setOpenGoogleMapView,
    showSiteMap,
    openConvertToContractModal,
    setOpenConvertToContractModal,
    hadnleOpenConvertToContractModal,
    openConfirmationDialog,
    setOpenConfirmationDialog,
    handleOpenConfirmationDialog,
    confirmationDialogProps,
    handleContractSubmit,
    deleteProductData,
    deleteSpecies,
    saveMapCoordinates,
    updateMapCoordinates,
    deleteMapCoordinates,
    allReqDropDown,
    openNewInfoAddDialog,
    setOpenNewInfoAddDialog,
    defaultValueNewInfoDialog,
    setDefaultValueNewInfoDialog,
    handleNewInfoSave,
    newSectionAddName,
    setNewSectionAddName,
    handleNewSectionAddClick,
    openRouteMapModal,
    setOpenRouteMapModal,
    openRouteView,
    allLocationDetails,
    setSavedRouteDetails,
    getCurrentSiteRouteDetails,
    savedRouteDetails,
    samples,
    samplesData,
    subCompSampleData,
    handleAction,
    handleSubCompSampleData,
    files,
    showFiles,
    closeFiles,
    openFilesDialog,
    handleFileChange,
    handleDeleteFiles,
    viewFile,
    isLoading,
    subCompartments,
    notes,
    handleSiteNotes,
    saveSiteNotes,
    saveSiteRef,
    handleSiteDelete,
    openDeleteModal,
    setOpenDeleteModal,
    selectedSiteId,
    setSelectedSiteId,
    quickAssessmentModal,
    showQuickAssessmentModal,
    closeQuickAssessmentModal,
    step,
    setStep,
    reference,
    setReference,
    handleContinue,
    handleQuickAssessmentSave,
    customerLocations,
    newSiteModal,
    showNewSiteModal,
    closeNewSiteModal,
    handleMapEditorClose, // 🗺️ Geofence handler for MapEditView
  };
};

export default useSiteAssessment;
